"use client";

import dayjs from "dayjs";
import { useCallback, useMemo } from "react";
import { useQueryStates } from "nuqs";
import { hotelParsers, DEFAULT_TRAVELERS } from "./shared/parsers";
import { getStableTodayIso } from "@/lib/date";

type SetSearchStateInput = {
  locationId: string;
  search?: string;
  lat?: number;
  lng?: number;
  start?: Date;
  end?: Date;
  adults: number;
  children: number;
  rooms: number;
};

export const useHotelQuery = () => {
  const [q, setQ] = useQueryStates(hotelParsers, { history: "replace" });

  const setLocationId = useCallback(
    (value: string) =>
      setQ({
        locationId: value.trim() || null,
        search: null,
        lat: null,
        lng: null,
      }),
    [setQ],
  );

  const setCoordinates = useCallback(
    (lat: number, lng: number) =>
      setQ({ locationId: null, search: null, lat: `${lat}`, lng: `${lng}` }),
    [setQ],
  );

  const setRange = useCallback(
    (from?: Date, to?: Date) =>
      setQ({
        start: from ? dayjs(from).format("YYYY-MM-DD") : null,
        end: to ? dayjs(to).format("YYYY-MM-DD") : null,
      }),
    [setQ],
  );

  const setTravelers = useCallback(
    (adults: number, children: number, rooms: number) =>
      setQ({ adults, children, rooms }),
    [setQ],
  );

  const setSearchState = useCallback(
    ({
      locationId,
      search,
      lat,
      lng,
      start,
      end,
      adults,
      children,
      rooms,
    }: SetSearchStateInput) => {
      const trimmedSearch = search?.trim();
      setQ({
        locationId: trimmedSearch ? null : locationId.trim() || null,
        search: trimmedSearch || null,
        lat: trimmedSearch || !Number.isFinite(lat) ? null : String(lat),
        lng: trimmedSearch || !Number.isFinite(lng) ? null : String(lng),
        start: start ? dayjs(start).format("YYYY-MM-DD") : null,
        end: end ? dayjs(end).format("YYYY-MM-DD") : null,
        adults,
        children,
        rooms,
      });
    },
    [setQ],
  );

  const setSearch = useCallback(
    (value: string) =>
      setQ({
        search: value.trim() || null,
        locationId: null,
        lat: null,
        lng: null,
      }),
    [setQ],
  );

  const setFilters = useCallback(
    (updates: Parameters<typeof setQ>[0]) => {
      if (!updates || typeof updates === "function") {
        setQ(updates);
        return;
      }
      const cleaned = Object.fromEntries(
        Object.entries(updates as Record<string, unknown>).map(
          ([key, value]) => [
            key,
            Array.isArray(value) && value.length === 0 ? null : value,
          ],
        ),
      ) as Parameters<typeof setQ>[0];
      setQ(cleaned);
    },
    [setQ],
  );

  const clearSearch = useCallback(
    () =>
      setQ({
        locationId: null,
        search: null,
        lat: null,
        lng: null,
        start: null,
        end: null,
        adults: null,
        children: null,
        rooms: null,
      }),
    [setQ],
  );

  const clearFilters = useCallback(
    () =>
      setQ({
        priceRange: null,
        propertyType: null,
        propertyRating: null,
        numberOfBeds: null,
        facilities: null,
        distance: null,
      }),
    [setQ],
  );

  const defaultToday = getStableTodayIso();

  const resolvedStart =
    q.start && dayjs(q.start).isValid() ? q.start : defaultToday;

  const resolvedEnd =
    q.end &&
    dayjs(q.end).isValid() &&
    dayjs(q.end).isAfter(dayjs(resolvedStart), "day")
      ? q.end
      : dayjs(resolvedStart).add(1, "day").format("YYYY-MM-DD");

  return {
    locationId: q.locationId ?? "",
    search: q.search ?? "",
    lat: q.lat ? parseFloat(q.lat) : undefined,
    lng: q.lng ? parseFloat(q.lng) : undefined,
    start: resolvedStart,
    end: resolvedEnd,
    adults: q.adults ?? DEFAULT_TRAVELERS.adults,
    children: q.children ?? DEFAULT_TRAVELERS.children,
    rooms: q.rooms ?? DEFAULT_TRAVELERS.rooms,

    priceRange: q.priceRange ?? [],
    propertyType: q.propertyType ?? [],
    propertyRating: q.propertyRating ?? [],
    numberOfBeds: q.numberOfBeds ?? [],
    facilities: q.facilities ?? [],
    distance: q.distance ?? [],

    setLocationId,
    setSearch,
    setCoordinates,
    setRange,
    setSearchState,
    setFilters,
    setTravelers,
    clearSearch,
    clearFilters,
  };
};

interface Hotel {
  id: string | number;
  title: string;
}

export function useHotelHref(hotel: Hotel, href?: string): string {
  const { adults, children, rooms, start, end } = useHotelQuery();

  const hotelStart = start || getStableTodayIso();
  const hotelEnd =
    end && dayjs(end).isAfter(dayjs(hotelStart), "day")
      ? end
      : dayjs(hotelStart).add(1, "day").format("YYYY-MM-DD");

  return useMemo(
    () =>
      href ??
      `/hotels/${hotel.id}?${new URLSearchParams({
        adults: `${adults}`,
        children: `${children}`,
        rooms: `${rooms}`,
        search: hotel.title,
        start: hotelStart,
        end: hotelEnd,
      })}`,
    [
      href,
      hotel.id,
      hotel.title,
      adults,
      children,
      rooms,
      hotelStart,
      hotelEnd,
    ],
  );
}
